import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Tag, Modal, Form, Input, Select, Space, message, Popconfirm, Tooltip, Descriptions, Divider, Radio } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  getConfigVersions,
  createConfig,
  updateConfig,
  publishConfig,
  archiveConfig,
  deleteConfig,
  duplicateConfig,
  getConfigById,
  SessionConfig,
} from '../../api/session-config';
import { getProviders, ProviderConfig, ProviderModel } from '../../api/providers';

interface KeyValuePair {
  key: string;
  value: string;
}

const KeyValueEditor: React.FC<{
  value: KeyValuePair[];
  onChange: (v: KeyValuePair[]) => void;
}> = ({ value, onChange }) => {
  const addPair = () => onChange([...value, { key: '', value: '' }]);
  const removePair = (index: number) => onChange(value.filter((_, i) => i !== index));
  const updatePair = (index: number, field: 'key' | 'value', newVal: string) =>
    onChange(value.map((pair, i) => (i === index ? { ...pair, [field]: newVal } : pair)));

  return (
    <div>
      {value.map((pair, index) => (
        <Space key={index} style={{ marginBottom: 8, display: 'flex' }}>
          <Input
            placeholder="参数名"
            value={pair.key}
            onChange={e => updatePair(index, 'key', e.target.value)}
            style={{ width: 200 }}
          />
          <Input
            placeholder="参数值"
            value={pair.value}
            onChange={e => updatePair(index, 'value', e.target.value)}
            style={{ width: 260 }}
          />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removePair(index)} />
        </Space>
      ))}
      {value.length === 0 && <div style={{ color: '#999', marginBottom: 8, fontSize: 13 }}>暂无自定义参数</div>}
      <Button type="dashed" onClick={addPair} icon={<PlusOutlined />} size="small">添加参数</Button>
    </div>
  );
};

function pairsToRecord(pairs: KeyValuePair[]): Record<string, string> {
  return pairs.reduce((acc, { key, value }) => {
    if (key.trim()) acc[key.trim()] = value;
    return acc;
  }, {} as Record<string, string>);
}

function recordToPairs(record: Record<string, any> | undefined | null): KeyValuePair[] {
  if (!record) return [];
  return Object.entries(record).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
  }));
}

function splitModelParams(jsonStr: string | null | undefined) {
  if (!jsonStr) return { stt: [] as KeyValuePair[], llm: [] as KeyValuePair[], multimodal: [] as KeyValuePair[] };
  try {
    const parsed = JSON.parse(jsonStr);
    return {
      stt: recordToPairs(parsed.stt),
      llm: recordToPairs(parsed.llm),
      multimodal: recordToPairs(parsed.multimodal),
    };
  } catch {
    return { stt: [], llm: [], multimodal: [] };
  }
}

function mergeModelParams(stt: KeyValuePair[], llm: KeyValuePair[], multimodal: KeyValuePair[]): string | null {
  const result: Record<string, Record<string, string>> = {};
  const sttObj = pairsToRecord(stt);
  const llmObj = pairsToRecord(llm);
  const mmObj = pairsToRecord(multimodal);
  if (Object.keys(sttObj).length > 0) result.stt = sttObj;
  if (Object.keys(llmObj).length > 0) result.llm = llmObj;
  if (Object.keys(mmObj).length > 0) result.multimodal = mmObj;
  return Object.keys(result).length > 0 ? JSON.stringify(result) : null;
}

const statusMap: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
  draft: { color: 'default', text: '草稿', icon: <ClockCircleOutlined /> },
  published: { color: 'success', text: '已发布', icon: <CheckCircleOutlined /> },
  archived: { color: 'warning', text: '已归档', icon: <StopOutlined /> },
};

const SessionConfigPage: React.FC = () => {
  const [versions, setVersions] = useState<SessionConfig[]>([]);
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState<{ visible: boolean; config: SessionConfig | null }>({
    visible: false,
    config: null,
  });
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState<{ visible: boolean; config: SessionConfig | null }>({
    visible: false,
    config: null,
  });
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const [createSttParams, setCreateSttParams] = useState<KeyValuePair[]>([]);
  const [createLlmParams, setCreateLlmParams] = useState<KeyValuePair[]>([]);
  const [createMultimodalParams, setCreateMultimodalParams] = useState<KeyValuePair[]>([]);
  const [editSttParams, setEditSttParams] = useState<KeyValuePair[]>([]);
  const [editLlmParams, setEditLlmParams] = useState<KeyValuePair[]>([]);
  const [editMultimodalParams, setEditMultimodalParams] = useState<KeyValuePair[]>([]);

  // 监听表单值变化，用于动态渲染模型下拉
  const createProcessingMode = Form.useWatch('processingMode', createForm);
  const createSttProviderCode = Form.useWatch('sttProviderCode', createForm);
  const createLlmProviderCode = Form.useWatch('llmProviderCode', createForm);
  const createMultimodalProviderCode = Form.useWatch('multimodalProviderCode', createForm);

  const editProcessingMode = Form.useWatch('processingMode', editForm);
  const editSttProviderCode = Form.useWatch('sttProviderCode', editForm);
  const editLlmProviderCode = Form.useWatch('llmProviderCode', editForm);
  const editMultimodalProviderCode = Form.useWatch('multimodalProviderCode', editForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [versionsData, providersData] = await Promise.all([
        getConfigVersions(),
        getProviders(),
      ]);
      setVersions(versionsData);
      setProviders(providersData);
    } catch {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      processingMode: 'stt_llm',
    });
    setCreateSttParams([]);
    setCreateLlmParams([]);
    setCreateMultimodalParams([]);
    setCreateModal(true);
  };

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      setSubmitting(true);
      await createConfig({
        processingMode: values.processingMode,
        promptPart1: values.promptPart1 || null,
        modelParams: mergeModelParams(createSttParams, createLlmParams, createMultimodalParams),
        sttProviderCode: values.sttProviderCode || null,
        sttModelId: values.sttModelId || null,
        llmProviderCode: values.llmProviderCode || null,
        llmModelId: values.llmModelId || null,
        multimodalProviderCode: values.multimodalProviderCode || null,
        multimodalModelId: values.multimodalModelId || null,
        remark: values.remark || '',
      });
      message.success('创建成功');
      setCreateModal(false);
      fetchData();
    } catch {
      // error handled by interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (config: SessionConfig) => {
    try {
      const fullConfig = await getConfigById(config.id);
      const { stt, llm, multimodal } = splitModelParams(fullConfig.modelParams);
      editForm.setFieldsValue({
        processingMode: fullConfig.processingMode,
        promptPart1: fullConfig.promptPart1 || undefined,
        sttProviderCode: fullConfig.sttProviderCode || undefined,
        sttModelId: fullConfig.sttModelId || undefined,
        llmProviderCode: fullConfig.llmProviderCode || undefined,
        llmModelId: fullConfig.llmModelId || undefined,
        multimodalProviderCode: fullConfig.multimodalProviderCode || undefined,
        multimodalModelId: fullConfig.multimodalModelId || undefined,
        remark: fullConfig.remark || '',
      });
      setEditSttParams(stt);
      setEditLlmParams(llm);
      setEditMultimodalParams(multimodal);
      setEditModal({ visible: true, config: fullConfig });
    } catch {
      message.error('获取配置详情失败');
    }
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      if (!editModal.config) return;
      setSubmitting(true);
      await updateConfig(editModal.config.id, {
        processingMode: values.processingMode,
        promptPart1: values.promptPart1 || null,
        modelParams: mergeModelParams(editSttParams, editLlmParams, editMultimodalParams),
        sttProviderCode: values.sttProviderCode || null,
        sttModelId: values.sttModelId || null,
        llmProviderCode: values.llmProviderCode || null,
        llmModelId: values.llmModelId || null,
        multimodalProviderCode: values.multimodalProviderCode || null,
        multimodalModelId: values.multimodalModelId || null,
        remark: values.remark || '',
      });
      message.success('更新成功');
      setEditModal({ visible: false, config: null });
      fetchData();
    } catch {
      // error handled by interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (config: SessionConfig) => {
    try {
      await publishConfig(config.id);
      message.success('发布成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleArchive = async (config: SessionConfig) => {
    try {
      await archiveConfig(config.id);
      message.success('归档成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleDelete = async (config: SessionConfig) => {
    try {
      await deleteConfig(config.id);
      message.success('删除成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleDuplicate = async (config: SessionConfig) => {
    try {
      await duplicateConfig(config.id);
      message.success('复制成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleViewDetail = async (config: SessionConfig) => {
    try {
      const fullConfig = await getConfigById(config.id);
      setDetailModal({ visible: true, config: fullConfig });
    } catch {
      message.error('获取配置详情失败');
    }
  };

  const getEnabledProviders = () => {
    return providers.filter(p => p.isEnabled);
  };

  const getModelsByProviderAndType = (providerCode: string | undefined, modelType: string): ProviderModel[] => {
    if (!providerCode) return [];
    const provider = providers.find(p => p.providerCode === providerCode);
    return provider?.models?.filter(m => m.isEnabled && m.modelType === modelType) || [];
  };

  const getAllModelsByProvider = (providerCode: string | undefined): ProviderModel[] => {
    if (!providerCode) return [];
    const provider = providers.find(p => p.providerCode === providerCode);
    return provider?.models?.filter(m => m.isEnabled) || [];
  };

  const renderConfigForm = (
    form: any,
    isEdit: boolean,
    processingMode?: string,
    sttProviderCode?: string,
    llmProviderCode?: string,
    multimodalProviderCode?: string,
    sttParams?: KeyValuePair[],
    llmParams?: KeyValuePair[],
    multimodalParams?: KeyValuePair[],
    onSttParamsChange?: (v: KeyValuePair[]) => void,
    onLlmParamsChange?: (v: KeyValuePair[]) => void,
    onMultimodalParamsChange?: (v: KeyValuePair[]) => void,
  ) => (
    <>
      <Form.Item
        label="System Prompt（第一部分）"
        name="promptPart1"
        tooltip="配置 AI 的角色设定和行为准则。留空则使用默认设定。"
      >
        <Input.TextArea rows={4} placeholder="你是一个专业的营养师助手。请分析用户描述的食物，提取营养信息。" />
      </Form.Item>

      <Form.Item
        label="处理模式"
        name="processingMode"
        rules={[{ required: true, message: '请选择处理模式' }]}
      >
        <Radio.Group disabled={isEdit && editModal.config?.status !== 'draft'}>
          <Radio.Button value="stt_llm">STT + LLM</Radio.Button>
          <Radio.Button value="multimodal">多模态模型</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {processingMode === 'stt_llm' && (
        <>
          <Divider orientation="left">语音识别 (STT)</Divider>
          <Form.Item label="STT Provider" name="sttProviderCode">
            <Select placeholder="选择 STT Provider" allowClear>
              {getEnabledProviders().map(p => (
                <Select.Option key={p.providerCode} value={p.providerCode}>
                  {p.providerName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="STT 模型" name="sttModelId">
            <Select placeholder="选择模型（留空使用默认）" allowClear>
              {getModelsByProviderAndType(sttProviderCode, 'stt').map(m => (
                <Select.Option key={m.id} value={m.id}>
                  {m.displayName || m.modelName}
                  {m.isDefault && <Tag color="blue" style={{ marginLeft: 4 }}>默认</Tag>}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#666' }}>STT 自定义参数</div>
            <KeyValueEditor value={sttParams || []} onChange={onSttParamsChange || (() => {})} />
          </div>

          <Divider orientation="left">大语言模型 (LLM)</Divider>
          <Form.Item label="LLM Provider" name="llmProviderCode">
            <Select placeholder="选择 LLM Provider" allowClear>
              {getEnabledProviders().map(p => (
                <Select.Option key={p.providerCode} value={p.providerCode}>
                  {p.providerName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="LLM 模型" name="llmModelId">
            <Select placeholder="选择模型（留空使用默认）" allowClear>
              {getModelsByProviderAndType(llmProviderCode, 'llm').map(m => (
                <Select.Option key={m.id} value={m.id}>
                  {m.displayName || m.modelName}
                  {m.isDefault && <Tag color="blue" style={{ marginLeft: 4 }}>默认</Tag>}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#666' }}>LLM 自定义参数</div>
            <KeyValueEditor value={llmParams || []} onChange={onLlmParamsChange || (() => {})} />
          </div>
        </>
      )}

      {processingMode === 'multimodal' && (
        <>
          <Divider orientation="left">多模态模型</Divider>
          <Form.Item label="多模态 Provider" name="multimodalProviderCode">
            <Select placeholder="选择多模态 Provider" allowClear>
              {getEnabledProviders().map(p => (
                <Select.Option key={p.providerCode} value={p.providerCode}>
                  {p.providerName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="多模态模型" name="multimodalModelId">
            <Select placeholder="选择模型（留空使用默认）" allowClear>
              {getModelsByProviderAndType(multimodalProviderCode, 'multimodal').map(m => (
                <Select.Option key={m.id} value={m.id}>
                  {m.displayName || m.modelName}
                  {m.isDefault && <Tag color="blue" style={{ marginLeft: 4 }}>默认</Tag>}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#666' }}>多模态自定义参数</div>
            <KeyValueEditor value={multimodalParams || []} onChange={onMultimodalParamsChange || (() => {})} />
          </div>
        </>
      )}

      <Form.Item label="备注" name="remark">
        <Input.TextArea rows={2} placeholder="版本说明（可选）" />
      </Form.Item>
    </>
  );

  const columns = [
    {
      title: '版本',
      dataIndex: 'version',
      render: (v: number) => <Tag>v{v}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (v: string) => {
        const s = statusMap[v] || { color: 'default', text: v, icon: null };
        return <Tag color={s.color} icon={s.icon}>{s.text}</Tag>;
      },
    },
    {
      title: '处理模式',
      dataIndex: 'processingMode',
      render: (v: string) => v === 'multimodal' ? '多模态模型' : 'STT + LLM',
    },
    {
      title: '使用模型',
      render: (_: any, record: SessionConfig) => {
        if (record.processingMode === 'multimodal') {
          const name = getModelName(record.multimodalProviderCode, record.multimodalModelId);
          return <Tag>{record.multimodalProviderCode ? `${record.multimodalProviderCode}: ${name}` : '默认'}</Tag>;
        }
        return (
          <Space size={4} wrap>
            <Tag>{record.sttProviderCode ? `${record.sttProviderCode}: ${getModelName(record.sttProviderCode, record.sttModelId)}` : 'STT: 默认'}</Tag>
            <Tag>{record.llmProviderCode ? `${record.llmProviderCode}: ${getModelName(record.llmProviderCode, record.llmModelId)}` : 'LLM: 默认'}</Tag>
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      render: (v: number) => new Date(v * 1000).toLocaleString(),
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAt',
      render: (v: number | null) => v ? new Date(v * 1000).toLocaleString() : '-',
    },
    {
      title: '操作',
      fixed: 'right' as const,
      render: (_: any, record: SessionConfig) => (
        <Space>
          <Tooltip title="查看详情">
            <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
              详情
            </Button>
          </Tooltip>
          {record.status === 'draft' && (
            <>
              <Tooltip title="编辑">
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                  编辑
                </Button>
              </Tooltip>
              <Popconfirm title="确定发布此版本？将替换当前发布版本" onConfirm={() => handlePublish(record)}>
                <Button type="link" size="small" icon={<CloudUploadOutlined />}>
                  发布
                </Button>
              </Popconfirm>
              <Popconfirm title="确定删除此草稿？" onConfirm={() => handleDelete(record)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
          {record.status === 'published' && (
            <Popconfirm title="确定归档此版本？归档后将停止使用" onConfirm={() => handleArchive(record)}>
              <Button type="link" size="small" icon={<InboxOutlined />}>
                归档
              </Button>
            </Popconfirm>
          )}
          {record.status === 'archived' && (
            <>
              <Tooltip title="编辑">
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                  编辑
                </Button>
              </Tooltip>
              <Popconfirm title="确定重新发布此归档版本？将替换当前发布版本" onConfirm={() => handlePublish(record)}>
                <Button type="link" size="small" icon={<CloudUploadOutlined />}>
                  重新发布
                </Button>
              </Popconfirm>
              <Popconfirm title="确定删除此归档版本？" onConfirm={() => handleDelete(record)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
              <Tooltip title="基于此版本创建新草稿">
                <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleDuplicate(record)}>
                  复制
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const getProviderName = (code?: string | null) => {
    if (!code) return '-';
    const p = providers.find(p => p.providerCode === code);
    return p?.providerName || code;
  };

  const getModelName = (providerCode?: string | null, modelId?: string | null) => {
    if (!providerCode || !modelId) return '默认';
    const models = getAllModelsByProvider(providerCode);
    const m = models.find(m => m.id === modelId);
    return m?.displayName || m?.modelName || '默认';
  };

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Card
          title={
            <span>
              会话配置版本管理{' '}
              <Tooltip title="同时只能有一个发布版本。新建版本为草稿状态，编辑后可发布。发布后会自动替换之前的发布版本（归档）。">
                <QuestionCircleOutlined style={{ color: '#999', fontSize: 14 }} />
              </Tooltip>
            </span>
          }
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新建版本
            </Button>
          }
          styles={{ body: { flex: 1, overflow: 'auto', padding: 24 } }}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Table
            dataSource={versions}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content', y: 'calc(100vh - 290px)' }}
          />
        </Card>
      </div>

      {/* 新建版本弹窗 */}
      <Modal
        title="新建配置版本"
        open={createModal}
        onOk={handleCreateSubmit}
        onCancel={() => setCreateModal(false)}
        width={640}
        confirmLoading={submitting}
      >
        <Form form={createForm} layout="vertical">
          {renderConfigForm(createForm, false, createProcessingMode, createSttProviderCode, createLlmProviderCode, createMultimodalProviderCode, createSttParams, createLlmParams, createMultimodalParams, setCreateSttParams, setCreateLlmParams, setCreateMultimodalParams)}
        </Form>
      </Modal>

      {/* 编辑版本弹窗 */}
      <Modal
        title={`编辑配置版本 v${editModal.config?.version || ''}`}
        open={editModal.visible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModal({ visible: false, config: null })}
        width={640}
        confirmLoading={submitting}
      >
        <Form form={editForm} layout="vertical">
          {renderConfigForm(editForm, true, editProcessingMode, editSttProviderCode, editLlmProviderCode, editMultimodalProviderCode, editSttParams, editLlmParams, editMultimodalParams, setEditSttParams, setEditLlmParams, setEditMultimodalParams)}
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={`配置版本详情 v${detailModal.config?.version || ''}`}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, config: null })}
        footer={null}
        width={640}
      >
        {detailModal.config && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="版本号">v{detailModal.config.version}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[detailModal.config.status]?.color}>
                {statusMap[detailModal.config.status]?.text}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="System Prompt（第一部分）">
              {detailModal.config.promptPart1 || '（使用默认设定）'}
            </Descriptions.Item>
            <Descriptions.Item label="处理模式">
              {detailModal.config.processingMode === 'multimodal' ? '多模态模型' : 'STT + LLM'}
            </Descriptions.Item>
            {detailModal.config.processingMode === 'stt_llm' && (
              <>
                <Descriptions.Item label="STT Provider">{getProviderName(detailModal.config.sttProviderCode)}</Descriptions.Item>
                <Descriptions.Item label="STT 模型">{getModelName(detailModal.config.sttProviderCode, detailModal.config.sttModelId)}</Descriptions.Item>
                {(() => {
                  const { stt } = splitModelParams(detailModal.config.modelParams);
                  return stt.length > 0 ? (
                    <Descriptions.Item label="STT 自定义参数">
                      {stt.map(p => <Tag key={p.key} style={{ marginBottom: 4 }}>{p.key}: {p.value}</Tag>)}
                    </Descriptions.Item>
                  ) : null;
                })()}
                <Descriptions.Item label="LLM Provider">{getProviderName(detailModal.config.llmProviderCode)}</Descriptions.Item>
                <Descriptions.Item label="LLM 模型">{getModelName(detailModal.config.llmProviderCode, detailModal.config.llmModelId)}</Descriptions.Item>
                {(() => {
                  const { llm } = splitModelParams(detailModal.config.modelParams);
                  return llm.length > 0 ? (
                    <Descriptions.Item label="LLM 自定义参数">
                      {llm.map(p => <Tag key={p.key} style={{ marginBottom: 4 }}>{p.key}: {p.value}</Tag>)}
                    </Descriptions.Item>
                  ) : null;
                })()}
              </>
            )}
            {detailModal.config.processingMode === 'multimodal' && (
              <>
                <Descriptions.Item label="多模态 Provider">{getProviderName(detailModal.config.multimodalProviderCode)}</Descriptions.Item>
                <Descriptions.Item label="多模态模型">{getModelName(detailModal.config.multimodalProviderCode, detailModal.config.multimodalModelId)}</Descriptions.Item>
                {(() => {
                  const { multimodal } = splitModelParams(detailModal.config.modelParams);
                  return multimodal.length > 0 ? (
                    <Descriptions.Item label="多模态自定义参数">
                      {multimodal.map(p => <Tag key={p.key} style={{ marginBottom: 4 }}>{p.key}: {p.value}</Tag>)}
                    </Descriptions.Item>
                  ) : null;
                })()}
              </>
            )}
            <Descriptions.Item label="备注">{detailModal.config.remark || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{new Date(detailModal.config.createdAt * 1000).toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="发布时间">
              {detailModal.config.publishedAt ? new Date(detailModal.config.publishedAt * 1000).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default SessionConfigPage;
